import { ChangeDetectionStrategy, inject, Component, input, OnInit, output, effect, signal } from '@angular/core';
import { LoggerService } from '@/core/services/logger.service';

import { AbstractControl, FormBuilder, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { CardContent } from '@/shared/components/card-content/card-content';
import { InputText } from 'primeng/inputtext';
import { InputMask } from 'primeng/inputmask';
import { Select } from 'primeng/select';
import { forkJoin, of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';

import { Pessoa } from '@/shared/models/pessoa-info.model';
import { PessoaResponseModel } from '@/shared/components/pessoa/pesquisa/pessoa.model';
import { MedicoService } from '@/core/api/sicom-api/registro/medico/medico.service';
import { MedicoRequest } from '@/core/api/sicom-api/registro/medico/medico.model';
import { DocumentosService } from './documentos.service';
import { PessoaSaveRequest } from '@/shared/models/pessoa-save-request.model';
import { DocumentosFormValue } from '../../medicos-form.types';
import { BotaoAcoes } from '@/shared/components/layout/botao-acoes/botao-acoes';
import { SelectEstado } from '@/shared/components/layout/select-estado/select-estado';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';
import { FormularioValidatorUtil } from '@/shared/utils/formulario-validator-util';
import { DateUtils } from '@/shared/utils/date-utils';
import { acaoMenuItem } from '@/shared/components/layout/botao-acoes/acao-menu-item';
import { AppMenuItem } from '@/core/permissions/permission.model';

@Component({
    selector: 'app-documentos',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ReactiveFormsModule, CardContent, InputText, InputMask, Select, BotaoAcoes, SelectEstado, CanDirective],
    templateUrl: './documentos.html',
    styleUrl: './documentos.scss'
})
/** Aba de documentos do médico (RG, título, RNE, militar, etc.). */
export class Documentos implements OnInit {
    private readonly logger = inject(LoggerService);
    pessoa = input<Pessoa | PessoaResponseModel>();

    salvar = output<{ pessoaPartial: Partial<PessoaSaveRequest>; documentosFormValue: DocumentosFormValue }>();

    private readonly fb = inject(FormBuilder);
    readonly documentosForm = this.fb.nonNullable.group({
        cpfNumero: ['', [Validators.required, this.cpfValidoValidator()]],
        rgNumero: ['', [this.rgValidoValidator(), Validators.maxLength(16)]],
        rgDataEmissao: ['', [this.dataMenorOuIgualHojeValidator()]],
        rgOrgaoEmissor: [''],
        rgUF: [''],
        tituloNumero: [''],
        tituloZona: [''],
        tituloSecao: [''],
        tituloUF: [''],
        rneNumero: [''],
        rneDataEmissao: ['', [this.dataMenorOuIgualHojeValidator()]],
        rneDataValidade: ['', [this.dataMenorOuIgualHojeValidator()]],
        passaporteNumero: [''],
        tipoRegistro: [''],
        certificadoMilitarNumero: [''],
        certificadoMilitarCategoria: [''],
        situacaoMilitar: [''],
        ministerio: ['']
    });
    modoEdicao: boolean = false;
    protected readonly roleDocumentosEditar = PERMISSOES.medicos.documentos.editar;

    get acoesFormulario(): AppMenuItem[] {
        return [
            acaoMenuItem({
                id: 'editar',
                label: 'Editar',
                icon: 'pi pi-pencil',
                cor: 'editar',
                disabled: this.modoEdicao,
                permission: this.roleDocumentosEditar,
                command: () => this.habilitarEdicao()
            })
        ];
    }

    /** Ver acoesFormulario: Salvar e Cancelar saem do menu e viram ação em destaque. */
    get itensEdicao(): AppMenuItem[] {
        const role = this.roleDocumentosEditar;
        return [
            acaoMenuItem({ id: 'salvar', label: 'Salvar', icon: 'pi pi-check', cor: 'salvar', permission: role, command: () => this.salvarDados() }),
            acaoMenuItem({ id: 'cancelar', label: 'Cancelar', icon: 'pi pi-times', cor: 'cancelar', permission: role, command: () => this.cancelarEdicao() })
        ];
    }

    carregando = signal(false);
    dominiosCarregados = signal(false);

    inEstrangeiro?: string;

    opcoesTipoRegistro = [
        { label: 'Permanente', value: 'PERMANENTE' },
        { label: 'Provisório', value: 'PROVISORIO' }
    ];

    ministerioOptions = [
        { label: 'Marinha', value: 'M' },
        { label: 'Aeronáutica', value: 'A' },
        { label: 'Exército', value: 'E' }
    ];

    situacaoMilitarOptions: { label: string; value: string }[] = [];

    // Opções de UF (inclui BR para manter compatibilidade com o seletor anterior)
    opcoesUF: { label: string; value: string }[] = [
        { label: 'Brasil', value: 'BR' },
        { label: 'Acre (AC)', value: 'AC' },
        { label: 'Alagoas (AL)', value: 'AL' },
        { label: 'Amapá (AP)', value: 'AP' },
        { label: 'Amazonas (AM)', value: 'AM' },
        { label: 'Bahia (BA)', value: 'BA' },
        { label: 'Ceará (CE)', value: 'CE' },
        { label: 'Distrito Federal (DF)', value: 'DF' },
        { label: 'Espírito Santo (ES)', value: 'ES' },
        { label: 'Goiás (GO)', value: 'GO' },
        { label: 'Maranhão (MA)', value: 'MA' },
        { label: 'Mato Grosso (MT)', value: 'MT' },
        { label: 'Mato Grosso do Sul (MS)', value: 'MS' },
        { label: 'Minas Gerais (MG)', value: 'MG' },
        { label: 'Pará (PA)', value: 'PA' },
        { label: 'Paraíba (PB)', value: 'PB' },
        { label: 'Paraná (PR)', value: 'PR' },
        { label: 'Pernambuco (PE)', value: 'PE' },
        { label: 'Piauí (PI)', value: 'PI' },
        { label: 'Rio de Janeiro (RJ)', value: 'RJ' },
        { label: 'Rio Grande do Norte (RN)', value: 'RN' },
        { label: 'Rio Grande do Sul (RS)', value: 'RS' },
        { label: 'Rondônia (RO)', value: 'RO' },
        { label: 'Roraima (RR)', value: 'RR' },
        { label: 'Santa Catarina (SC)', value: 'SC' },
        { label: 'São Paulo (SP)', value: 'SP' },
        { label: 'Sergipe (SE)', value: 'SE' },
        { label: 'Tocantins (TO)', value: 'TO' }
    ];

    constructor(
        private medicoService: MedicoService,
        private documentosService: DocumentosService
    ) {
        this.inicializarFormulario();

        effect(() => {
            const pessoaAtual = this.pessoa();
            const dominiosProntos = this.dominiosCarregados();

            if (pessoaAtual) {
                if (!this.modoEdicao) {
                    this.preencherCpfDaPessoa(pessoaAtual);

                    if (dominiosProntos && this.isPessoaRequest(pessoaAtual)) {
                        this.carregarMedico(pessoaAtual);
                    }
                }
            }
        });
    }

    ngOnInit(): void {
        this.documentosForm.disable({ emitEvent: false });
        this.carregarDominios();
    }

    private inicializarFormulario(): void {
        this.documentosForm.get('rgNumero')?.valueChanges.subscribe((v) => {
            this.atualizarValidadeDependente(v, ['rgDataEmissao', 'rgOrgaoEmissor', 'rgUF']);
        });

        this.documentosForm.get('tituloNumero')?.valueChanges.subscribe((v) => {
            this.atualizarValidadeDependente(v, ['tituloZona', 'tituloSecao', 'tituloUF']);
        });
    }

    private atualizarValidadeDependente(valor: unknown, camposDependentes: string[]): void {
        const preenchido = !!valor && String(valor).trim() !== '';
        camposDependentes.forEach((depName) => {
            const depControl = this.documentosForm.get(depName);
            if (depControl) {
                if (preenchido) {
                    depControl.addValidators(Validators.required);
                } else {
                    depControl.removeValidators(Validators.required);
                }
                depControl.updateValueAndValidity({ emitEvent: false });
            }
        });
    }

    private carregarDominios(): void {
        this.carregando.set(true);

        forkJoin({
            situacoesMilitares: this.documentosService.listarSituacoesMilitares().pipe(catchError(() => of([])))
        })
            .pipe(
                finalize(() => {
                    this.carregando.set(false);
                    this.dominiosCarregados.set(true);
                })
            )
            .subscribe({
                next: (res) => {
                    this.situacaoMilitarOptions = (res.situacoesMilitares || []).map((i) => ({ label: i.descricao, value: i.codigo }));
                },
                error: (err) => this.logger.error('Erro crítico ao carregar domínios', err)
            });
    }

    private carregarMedico(pessoa: Pessoa | PessoaResponseModel): void {
        if (!this.isPessoaRequest(pessoa)) {
            return;
        }

        this.carregando.set(true);

        const request: Pessoa = { id: pessoa.id, uf: pessoa.uf };

        this.medicoService
            .buscar(request)
            .pipe(finalize(() => this.carregando.set(false)))
            .subscribe({
                next: (medico: MedicoRequest) => this.preencherFormulario(medico),
                error: (err) => this.logger.error('Erro ao buscar dados do médico:', err)
            });
    }

    private preencherFormulario(m: MedicoRequest): void {
        if (!this.documentosForm) return;

        this.inEstrangeiro = m.inEstrangeiro;

        this.documentosForm.patchValue(
            {
                rgNumero: m.nuRg ?? '',
                rgDataEmissao: m.dtExpedicaoRg ?? '',
                rgOrgaoEmissor: m.nmOrgaoExpedidor ?? '',
                rgUF: m.sgUfRgExpedida ?? '',

                tituloNumero: m.nuTituloEleitor ?? '',
                tituloZona: m.dsZona ?? '',
                tituloSecao: m.dsSecao ?? '',
                tituloUF: m.sgUfTituloEleitor ?? '',

                rneNumero: m.nuCedulaEstrangeira ?? '',
                rneDataEmissao: m.dtCedulaEstrangeira ?? '',
                rneDataValidade: m.dtValCedulaEstrangeira ?? '',
                passaporteNumero: m.nuPassaporte ?? '',

                certificadoMilitarNumero: m.nuCertificadoMilitar ?? '',
                certificadoMilitarCategoria: m.nmRegiao ?? '',
                situacaoMilitar: m.inSituacaoMilitar ?? '',
                ministerio: m.inMinisterio ?? this.mapMinisterioNomeParaCodigo(m.nmMinisterio) ?? '',

                tipoRegistro: this.mapTipoRegistro(m.inEstrProvPerm) ?? ''
            },
            { emitEvent: false }
        );

        this.atualizarValidadeDependente(m.nuRg, ['rgDataEmissao', 'rgOrgaoEmissor', 'rgUF']);
        this.atualizarValidadeDependente(m.nuTituloEleitor, ['tituloZona', 'tituloSecao', 'tituloUF']);

        if (!this.modoEdicao) {
            this.documentosForm.disable({ emitEvent: false });
        }
    }

    habilitarEdicao(): void {
        this.modoEdicao = true;
        this.documentosForm.enable({ emitEvent: false });
    }

    salvarDados(): void {
        if (!this.documentosForm.valid) {
            this.documentosForm.markAllAsTouched();
            return;
        }

        const pessoaPartial: Partial<PessoaSaveRequest> = {
            cpfOuCnpj: this.documentosForm.get('cpfNumero')?.value || undefined
        };

        this.salvar.emit({ pessoaPartial, documentosFormValue: this.documentosForm.value });
    }

    cancelarEdicao(): void {
        this.modoEdicao = false;
        this.documentosForm.disable({ emitEvent: false });

        const pessoaAtual = this.pessoa();
        this.preencherCpfDaPessoa(pessoaAtual);
        if (pessoaAtual && this.isPessoaRequest(pessoaAtual)) {
            this.carregarMedico(pessoaAtual);
        }
    }

    private isPessoaRequest(pessoa: Pessoa | PessoaResponseModel | undefined): pessoa is Pessoa {
        return !!pessoa && typeof (pessoa as { id?: unknown }).id === 'number' && typeof (pessoa as { uf?: unknown }).uf === 'string';
    }

    private isPessoaResponseModel(pessoa: Pessoa | PessoaResponseModel | undefined): pessoa is PessoaResponseModel {
        return !!pessoa && 'registro' in pessoa;
    }

    private preencherCpfDaPessoa(pessoaAtual?: Pessoa | PessoaResponseModel): void {
        if (!this.documentosForm || !this.isPessoaResponseModel(pessoaAtual)) return;

        const cpf = pessoaAtual.registro;
        if (cpf) {
            this.documentosForm.patchValue({ cpfNumero: cpf }, { emitEvent: false });
        }
    }

    private mapTipoRegistro(valor?: string): string | undefined {
        if (!valor) return undefined;
        return valor.toUpperCase().startsWith('V') ? 'PROVISORIO' : 'PERMANENTE';
    }

    private mapMinisterioNomeParaCodigo(nome?: string): string | undefined {
        if (!nome) return undefined;
        const n = nome
            .toString()
            .trim()
            .toUpperCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '');

        if (n === 'MARINHA') return 'M';
        if (n === 'AERONAUTICA') return 'A';
        if (n === 'EXERCITO') return 'E';
        return undefined;
    }

    get mostrarRne(): boolean {
        const ind = this.inEstrangeiro?.toUpperCase();
        const rne = this.documentosForm?.get('rneNumero')?.value;
        const rneVazio = !rne || String(rne).trim() === '';

        if (this.modoEdicao) {
            if (ind !== 'B') return true;
            return !rneVazio;
        }

        return !(ind === 'B' && rneVazio);
    }

    /** Valida o RG: apenas dígitos, com dígito verificador 'X'/'x' opcional no final. */
    private rgValidoValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const valor = String(control.value ?? '').trim();
            if (!valor) {
                return null;
            }

            return /^\d+[Xx]?$/.test(valor) ? null : { rgInvalido: true };
        };
    }

    private cpfValidoValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const valor = String(control.value ?? '').trim();
            if (!valor) {
                return null;
            }

            return FormularioValidatorUtil.cpfValido(valor) ? null : { cpfInvalido: true };
        };
    }

    private dataMenorOuIgualHojeValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const valor = String(control.value ?? '').trim();
            if (!valor) {
                return null;
            }

            if (!DateUtils.isDataValida(valor)) {
                return { dataInvalida: true };
            }

            return FormularioValidatorUtil.dataMaiorQueHoje(valor) ? { dataMaiorQueHojeInvalida: true } : null;
        };
    }

    campoInvalido(nomeCampo: string): boolean {
        const campo = this.documentosForm?.get(nomeCampo);
        return !!campo && campo.invalid && (campo.touched || campo.dirty);
    }

    mensagemErroCampo(nomeCampo: string): string | null {
        const campo = this.documentosForm?.get(nomeCampo);
        if (!campo?.errors) return null;

        if (campo.errors['required']) return 'Campo obrigatório.';
        if (campo.errors['cpfInvalido']) return 'CPF inválido.';
        if (campo.errors['rgInvalido']) return 'RG deve conter apenas números (e opcionalmente X no final).';
        if (campo.errors['maxlength']) return `Máximo de ${campo.errors['maxlength'].requiredLength} caracteres.`;
        if (campo.errors['dataMaiorQueHojeInvalida']) return 'A data deve ser menor ou igual a hoje.';
        return 'Valor inválido.';
    }
}
